import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import CaptureGuideDialog from "@/components/autoescuela/CaptureGuideDialog";
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  Camera,
  Send,
  ShieldCheck,
  ClockIcon,
  User,
  MapPin,
  Phone,
  Mail,
  FileImage,
  RefreshCw,
  FilePlus2,
  Paperclip,
  Upload,
  FileText,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api`, timeout: 60000 });

const MAX_UPLOAD_BYTES = 6 * 1024 * 1024;

/**
 * Resize a captured photo (client-side) so uploads stay under the 6MB cap
 * enforced by the backend. Keeps aspect ratio, targets ~1600px long side.
 */
async function fileToJpegDataUrl(file, maxSide = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Imagen no válida"));
      img.onload = () => {
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function PhotoField({ label, hint, kind, uploadedPreview, onUpload, disabled, testid }) {
  const inputRef = useRef(null);
  const fileRef = useRef(null);   // V1.78 · subida directa de archivo (PDF/JPG/PNG)
  const [busy, setBusy] = useState(false);
  const [localPreview, setLocalPreview] = useState(uploadedPreview || "");
  const [pdfName, setPdfName] = useState("");
  // V1.41 · Guía de encuadre previa a la cámara nativa.
  const [guideOpen, setGuideOpen] = useState(false);
  const docTypeByKind = {
    dni_front: "dni-front",
    dni_back: "dni-back",
    psicotecnico: "psicotecnico",
  };
  const docType = docTypeByKind[kind] || "contrato";
  const isPdf = !!pdfName || localPreview.startsWith("data:application/pdf");

  useEffect(() => {
    if (uploadedPreview) setLocalPreview(uploadedPreview);
  }, [uploadedPreview]);

  const pick = () => inputRef.current?.click();
  const pickFile = () => fileRef.current?.click();
  const openGuide = () => setGuideOpen(true);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const dataUrl = await fileToJpegDataUrl(f);
      if (dataUrl.length > MAX_UPLOAD_BYTES) {
        toast.error("La foto es demasiado grande. Inténtalo de nuevo con menor calidad.");
        return;
      }
      setPdfName("");
      setLocalPreview(dataUrl);
      await onUpload(kind, dataUrl);
    } catch (err) {
      toast.error(err?.message || "No se pudo procesar la foto");
    } finally {
      setBusy(false);
    }
  };

  // V1.78 · Subida directa de un archivo que el alumno ya tiene preparado
  // (PDF, JPG o PNG). Las imágenes se redimensionan igual que la foto; el
  // PDF se envía tal cual (sin tocar el flujo de PDF existente).
  const onPickedFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const isPdf = f.type === "application/pdf" || /\.pdf$/i.test(f.name || "");
    if (!isPdf && !/^image\//i.test(f.type)) {
      toast.error("Formato no admitido. Sube un PDF, JPG o PNG.");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = isPdf
        ? await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onerror = () => reject(new Error("No se pudo leer el archivo"));
            r.onload = () => resolve(r.result);
            r.readAsDataURL(f);
          })
        : await fileToJpegDataUrl(f);
      if (dataUrl.length > MAX_UPLOAD_BYTES) {
        toast.error("El archivo es demasiado grande (máximo 6 MB).");
        return;
      }
      setPdfName(isPdf ? (f.name || "documento.pdf") : "");
      setLocalPreview(dataUrl);
      await onUpload(kind, dataUrl);
    } catch (err) {
      toast.error(err?.message || "No se pudo procesar el archivo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-4" data-testid={`photo-field-${kind}`}>
      <div className="flex items-start gap-2 mb-2">
        <FileImage className="h-4 w-4 mt-0.5 text-orange-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-neutral-900">{label}</div>
          {hint && <div className="text-[11px] text-neutral-500 mt-0.5 leading-snug">{hint}</div>}
        </div>
      </div>

      {localPreview ? (
        isPdf ? (
          <div className="relative rounded-xl border border-neutral-200 bg-neutral-50 p-4 mb-2 flex items-center gap-3" data-testid={`pdf-preview-${kind}`}>
            <FileText className="h-7 w-7 text-red-500 shrink-0" />
            <div className="min-w-0">
              <div className="text-xs font-bold text-neutral-800 truncate">{pdfName || "documento.pdf"}</div>
              <div className="text-[11px] text-neutral-500">PDF subido correctamente</div>
            </div>
            <div className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-600/90 text-white text-[10px] font-bold px-2 py-0.5 shadow">
              <CheckCircle2 className="h-3 w-3" /> Subido
            </div>
          </div>
        ) : (
          <div className="relative rounded-xl overflow-hidden border border-neutral-200 bg-neutral-50 mb-2">
            <img
              src={localPreview}
              alt={label}
              className="w-full max-h-56 object-contain"
              data-testid={`photo-preview-${kind}`}
            />
            <div className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-emerald-600/90 text-white text-[10px] font-bold px-2 py-0.5 shadow">
              <CheckCircle2 className="h-3 w-3" /> Subida
            </div>
          </div>
        )
      ) : (
        <div className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-6 text-center mb-2">
          <Camera className="h-6 w-6 mx-auto text-neutral-400 mb-1" />
          <div className="text-xs text-neutral-500">Sin foto ni archivo aún</div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onFile}
        className="hidden"
        data-testid={testid || `photo-input-${kind}`}
      />
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/*,application/pdf"
        onChange={onPickedFile}
        className="hidden"
        data-testid={`file-input-${kind}`}
      />
      <button
        type="button"
        onClick={openGuide}
        disabled={busy || disabled}
        className="w-full h-11 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40 transition-colors"
        data-testid={`photo-take-${kind}`}
      >
        {busy ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Subiendo…
          </>
        ) : localPreview && !isPdf ? (
          <>
            <RefreshCw className="h-4 w-4" /> Cambiar foto
          </>
        ) : (
          <>
            <Camera className="h-4 w-4" /> Hacer foto
          </>
        )}
      </button>
      {/* V1.78 · Módulo adicional: el alumno ya tiene el documento preparado */}
      <button
        type="button"
        onClick={pickFile}
        disabled={busy || disabled}
        className="mt-2 w-full h-11 rounded-xl border-2 border-dashed border-neutral-300 bg-neutral-50 hover:bg-neutral-100 text-neutral-700 text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40 transition-colors"
        data-testid={`file-upload-${kind}`}
      >
        <Upload className="h-4 w-4 text-neutral-500" />
        {localPreview ? "Cambiar archivo (PDF, JPG o PNG)" : "¿Ya lo tienes en PDF o imagen? Subir archivo"}
      </button>
      <div className="mt-1 text-center text-[10px] text-neutral-400">
        Admite PDF, JPG o PNG · máx 6 MB
      </div>
      <CaptureGuideDialog
        open={guideOpen}
        docType={docType}
        onCancel={() => setGuideOpen(false)}
        onContinue={() => { setGuideOpen(false); pick(); }}
      />
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, type = "text", required, testid, inputMode }) {
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        inputMode={inputMode}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-11 rounded-xl border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        data-testid={testid}
      />
    </div>
  );
}

// V1.33-D · Selector + uploader para "Enviar otro documento".
// Permite al alumno enviar documentos adicionales al expediente en
// cualquier momento (durante o después del alta). Se sube a OneDrive
// via `/public/alta/{token}/document-generic` con Pillow auto-prep.
const EXTRA_DOC_TYPES = [
  { key: "solicitud_aptitud",   label: "Solicitud de Aptitud (firmada)" },
  { key: "traslado",             label: "Solicitud de Traslado (firmada)" },
  { key: "autorizacion",         label: "Autorización" },
  { key: "autorizacion_menor",   label: "Autorización de menor" },
  { key: "permiso_residencia",   label: "Permiso de residencia / NIE" },
  { key: "pasaporte",            label: "Pasaporte" },
  { key: "foto_carnet",          label: "Foto tamaño carnet" },
  { key: "certificado_medico",   label: "Certificado médico" },
  { key: "autoescuela_anterior", label: "Documento autoescuela anterior" },
  { key: "canje_permiso",        label: "Canje de permiso" },
  { key: "justificante_pago",    label: "Justificante de pago" },
  { key: "transferencia_bancaria", label: "Resguardo de transferencia bancaria" },
  { key: "mandato_sepa",         label: "Mandato SEPA" },
  { key: "otros",                label: "Otro documento (texto libre)" },
];

function ExtraDocumentUploader({ token, onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState("");
  const [freeLabel, setFreeLabel] = useState("");
  const [preview, setPreview] = useState("");
  const [lastSent, setLastSent] = useState([]);

  const pick = () => {
    if (!kind) {
      toast.error("Elige primero el tipo de documento");
      return;
    }
    inputRef.current?.click();
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      // Si es PDF, no procesamos client-side; sino generamos JPEG optimizado.
      let dataUrl;
      if (f.type === "application/pdf") {
        dataUrl = await new Promise((resolve, reject) => {
          const rd = new FileReader();
          rd.onerror = () => reject(new Error("No se pudo leer el archivo"));
          rd.onload = () => resolve(rd.result);
          rd.readAsDataURL(f);
        });
      } else {
        dataUrl = await fileToJpegDataUrl(f);
      }
      if (dataUrl.length > MAX_UPLOAD_BYTES) {
        toast.error("El archivo es demasiado grande (máx 6 MB)");
        return;
      }
      setPreview(dataUrl);
      const finalKind = kind === "otros" ? "otros" : kind;
      const label = kind === "otros" ? (freeLabel.trim() || "Documento") : "";
      const { data } = await publicApi.post(`/public/alta/${token}/document-generic`, {
        kind: finalKind,
        label,
        data_url: dataUrl,
      });
      const kindLabel = EXTRA_DOC_TYPES.find((t) => t.key === kind)?.label || finalKind;
      setLastSent((arr) => [
        { at: new Date().toISOString(), kind: finalKind, label: label || kindLabel },
        ...arr,
      ].slice(0, 8));
      setPreview("");
      setKind("");
      setFreeLabel("");
      toast.success(`✅ ${kindLabel} enviado correctamente`);
      onUploaded?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "No se pudo enviar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="bg-white rounded-3xl shadow-sm border border-neutral-100 p-5 sm:p-7 space-y-3"
      data-testid="alta-extra-doc-section"
    >
      <div className="flex items-center gap-2 mb-1">
        <FilePlus2 className="h-4 w-4 text-orange-500" />
        <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">
          Enviar otro documento
        </h2>
      </div>
      <p className="text-[11px] text-neutral-500 leading-snug">
        ¿Necesitas mandar algún documento adicional a la autoescuela? Elige el tipo
        y súbelo desde tu móvil. Puedes hacerlo tantas veces como necesites.
      </p>

      <div>
        <label className="block text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
          Tipo de documento
        </label>
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          className="w-full h-11 rounded-xl border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          data-testid="alta-extra-kind"
        >
          <option value="">Selecciona…</option>
          {EXTRA_DOC_TYPES.map((t) => (
            <option key={t.key} value={t.key}>{t.label}</option>
          ))}
        </select>
      </div>

      {kind === "otros" && (
        <TextField
          label="Nombre del documento"
          value={freeLabel}
          onChange={setFreeLabel}
          placeholder="Ej: Justificante de residencia, Notas de examen…"
          testid="alta-extra-free-label"
        />
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        onChange={onFile}
        className="hidden"
        data-testid="alta-extra-input"
      />
      <button
        type="button"
        onClick={pick}
        disabled={busy || !kind}
        data-testid="alta-extra-send"
        className="w-full h-12 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40 transition-colors"
      >
        {busy ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> Enviando…</>
        ) : (
          <><Paperclip className="h-4 w-4" /> Elegir archivo y enviar</>
        )}
      </button>

      {preview && (
        <div className="rounded-xl overflow-hidden border border-neutral-200 bg-neutral-50">
          <img src={preview} alt="preview" className="w-full max-h-40 object-contain" />
        </div>
      )}

      {lastSent.length > 0 && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 space-y-1"
             data-testid="alta-extra-sent-list">
          <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-bold">
            Documentos enviados en esta sesión
          </div>
          {lastSent.map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-emerald-900">
              <CheckCircle2 className="h-3 w-3" />
              <span className="truncate">{s.label}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function AltaDigitalPage() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("pending");
  const [studentName, setStudentName] = useState("");
  const [studentId, setStudentId] = useState("");

  const [form, setForm] = useState({
    name: "",
    primer_apellido: "",
    segundo_apellido: "",
    dni: "",
    birth_date: "",
    nacionalidad: "",
    pais_nacimiento: "",
    tipo_via: "",
    nombre_via: "",
    numero_via: "",
    bloque: "",
    portal: "",
    escalera: "",
    planta: "",
    puerta: "",
    codigo_postal: "",
    localidad: "",
    municipio: "",
    provincia: "",
    phone: "",
    telefono_emergencia: "",
    email: "",
  });

  const [consentLopd, setConsentLopd] = useState(false);
  const [consentComms, setConsentComms] = useState(false);
  const [consentTram, setConsentTram] = useState(false);
  const [psicoDeferred, setPsicoDeferred] = useState(false);

  const [uploads, setUploads] = useState({}); // { dni_front: dataUrl, dni_back: dataUrl, psicotecnico: dataUrl }
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await publicApi.get(`/public/alta/${token}`);
      setStatus(data.status);
      setStudentName(data.student_name || "");
      setStudentId(data.student_id || "");
      const p = data.prefill || {};
      setForm((f) => ({
        ...f,
        name: p.name || "",
        primer_apellido: p.primer_apellido || "",
        segundo_apellido: p.segundo_apellido || "",
        dni: p.dni || "",
        birth_date: p.birth_date || "",
        nacionalidad: p.nacionalidad || "",
        pais_nacimiento: p.pais_nacimiento || "",
        tipo_via: p.tipo_via || "",
        nombre_via: p.nombre_via || "",
        numero_via: p.numero_via || "",
        bloque: p.bloque || "",
        portal: p.portal || "",
        escalera: p.escalera || "",
        planta: p.planta || "",
        puerta: p.puerta || "",
        codigo_postal: p.codigo_postal || "",
        localidad: p.localidad || "",
        municipio: p.municipio || "",
        provincia: p.provincia || "",
        phone: p.phone || "",
        telefono_emergencia: p.telefono_emergencia || "",
        email: p.email || "",
      }));
    } catch (err) {
      setError(err?.response?.data?.detail || "El enlace no es válido o ha caducado.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const uploadDocument = async (kind, dataUrl) => {
    try {
      await publicApi.post(`/public/alta/${token}/document`, { kind, data_url: dataUrl });
      setUploads((u) => ({ ...u, [kind]: dataUrl }));
      toast.success("Foto subida correctamente");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir la foto");
      throw err;
    }
  };

  const submit = async () => {
    if (!consentLopd || !consentComms || !consentTram) {
      toast.error("Debes aceptar los tres consentimientos");
      return;
    }
    // Basic validation
    if (!form.name.trim() || !form.primer_apellido.trim() || !form.dni.trim() || !form.phone.trim()) {
      toast.error("Nombre, primer apellido, DNI y teléfono son obligatorios");
      return;
    }
    setSubmitting(true);
    try {
      await publicApi.post(`/public/alta/${token}/submit`, {
        ...form,
        psicotecnico_deferred: psicoDeferred,
        consent_lopd: true,
        consent_comms: true,
        consent_tramitacion: true,
      });
      setStatus("completed");
      toast.success("¡Ficha enviada correctamente!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar la ficha");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-lg p-6 sm:p-8 text-center" data-testid="alta-error">
          <div className="inline-flex items-center gap-2 rounded-full bg-red-100 text-red-800 text-xs font-semibold px-3 py-1 mb-3">
            <AlertCircle className="h-3.5 w-3.5" /> Enlace no válido
          </div>
          <h1 className="text-2xl font-bold mb-3">No se puede abrir el formulario</h1>
          <p className="text-neutral-600">{error}</p>
        </div>
      </div>
    );
  }

  if (status === "completed" || status === "expired") {
    const isDone = status === "completed";
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4 py-10">
        <div className="max-w-lg w-full bg-white rounded-3xl shadow-lg p-6 sm:p-8 text-center" data-testid="alta-done">
          <div className={"inline-flex items-center gap-2 rounded-full text-xs font-semibold px-3 py-1 mb-3 " + (isDone ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800")}>
            {isDone ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
            {isDone ? "Ficha completada" : "Enlace caducado"}
          </div>
          <h1 className="text-2xl font-bold mb-3">
            {isDone ? `¡Gracias, ${studentName || "alumno"}!` : "Enlace caducado"}
          </h1>
          <p className="text-neutral-600 leading-relaxed">
            {isDone
              ? "Hemos recibido tu alta digital. Nuestro equipo la validará en breve y te avisaremos por WhatsApp cuando esté todo en orden."
              : "Este enlace ha caducado. Contacte con Natur Autoescuela para obtener uno nuevo."}
          </p>
          {isDone && psicoDeferred && (
            <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex items-start gap-2">
              <ClockIcon className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Recuerda subir el <b>psicotécnico</b> en cuanto lo tengas. Te enviaremos un recordatorio en 72&nbsp;horas.
              </span>
            </div>
          )}
        </div>
        {/* V1.33-D · Post-envío: permitir enviar más documentos */}
        {isDone && (
          <div className="max-w-lg w-full mt-4">
            <ExtraDocumentUploader token={token} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-10 space-y-4">
        {/* Header */}
        <div className="bg-white rounded-3xl shadow-sm border border-neutral-100 p-5 sm:p-7">
          <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">Natur Autoescuela · Alta Digital</div>
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight mt-1">
            Hola{studentName ? `, ${studentName}` : ""} 👋
          </h1>
          <p className="text-neutral-600 mt-2 leading-relaxed text-sm">
            Completa este formulario desde tu móvil para dar de alta tu expediente. Necesitaremos algunos datos y una <b>foto de tu DNI</b> por ambas caras.
          </p>
        </div>

        {/* Personal data */}
        <section className="bg-white rounded-3xl shadow-sm border border-neutral-100 p-5 sm:p-7 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <User className="h-4 w-4 text-orange-500" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">Datos personales</h2>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <TextField label="Nombre" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="alta-name" />
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Primer apellido" value={form.primer_apellido} onChange={(v) => setForm({ ...form, primer_apellido: v })} required testid="alta-apellido1" />
              <TextField label="Segundo apellido" value={form.segundo_apellido} onChange={(v) => setForm({ ...form, segundo_apellido: v })} testid="alta-apellido2" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <TextField label="DNI / NIE" value={form.dni} onChange={(v) => setForm({ ...form, dni: v.toUpperCase() })} required placeholder="00000000A" testid="alta-dni" />
              <TextField label="Fecha nacimiento" type="date" value={form.birth_date} onChange={(v) => setForm({ ...form, birth_date: v })} testid="alta-birthdate" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Nacionalidad" value={form.nacionalidad} onChange={(v) => setForm({ ...form, nacionalidad: v })} placeholder="Española" testid="alta-nacionalidad" />
              <TextField label="País de nacimiento" value={form.pais_nacimiento} onChange={(v) => setForm({ ...form, pais_nacimiento: v })} placeholder="España" testid="alta-pais-nacimiento" />
            </div>
          </div>
        </section>

        {/* Address */}
        <section className="bg-white rounded-3xl shadow-sm border border-neutral-100 p-5 sm:p-7 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <MapPin className="h-4 w-4 text-orange-500" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">Dirección</h2>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <TextField label="Tipo vía" value={form.tipo_via} onChange={(v) => setForm({ ...form, tipo_via: v })} placeholder="Calle" testid="alta-tipovia" />
            <div className="col-span-2">
              <TextField label="Nombre vía" value={form.nombre_via} onChange={(v) => setForm({ ...form, nombre_via: v })} testid="alta-nombrevia" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Número" value={form.numero_via} onChange={(v) => setForm({ ...form, numero_via: v })} inputMode="numeric" testid="alta-numvia" />
            <TextField label="Bloque" value={form.bloque} onChange={(v) => setForm({ ...form, bloque: v })} testid="alta-bloque" />
          </div>
          <div className="grid grid-cols-4 gap-3">
            <TextField label="Portal" value={form.portal} onChange={(v) => setForm({ ...form, portal: v })} testid="alta-portal" />
            <TextField label="Esc." value={form.escalera} onChange={(v) => setForm({ ...form, escalera: v })} testid="alta-escalera" />
            <TextField label="Planta" value={form.planta} onChange={(v) => setForm({ ...form, planta: v })} testid="alta-planta" />
            <TextField label="Puerta" value={form.puerta} onChange={(v) => setForm({ ...form, puerta: v })} testid="alta-puerta" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Código postal" value={form.codigo_postal} onChange={(v) => setForm({ ...form, codigo_postal: v })} inputMode="numeric" testid="alta-cp" />
            <TextField label="Localidad" value={form.localidad} onChange={(v) => setForm({ ...form, localidad: v })} testid="alta-localidad" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Municipio" value={form.municipio} onChange={(v) => setForm({ ...form, municipio: v })} testid="alta-municipio" />
            <TextField label="Provincia" value={form.provincia} onChange={(v) => setForm({ ...form, provincia: v })} testid="alta-provincia" />
          </div>
        </section>

        {/* Contact */}
        <section className="bg-white rounded-3xl shadow-sm border border-neutral-100 p-5 sm:p-7 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <Phone className="h-4 w-4 text-orange-500" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">Contacto</h2>
          </div>
          <TextField label="Teléfono" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} type="tel" inputMode="tel" required testid="alta-phone" />
          <TextField label="Teléfono de emergencia" value={form.telefono_emergencia} onChange={(v) => setForm({ ...form, telefono_emergencia: v })} type="tel" inputMode="tel" placeholder="Familiar o persona de contacto" testid="alta-emergencia" />
          <TextField label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" inputMode="email" testid="alta-email" />
        </section>

        {/* Documents */}
        <section className="bg-white rounded-3xl shadow-sm border border-neutral-100 p-5 sm:p-7 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <Camera className="h-4 w-4 text-orange-500" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">Documentos</h2>
          </div>

          <PhotoField
            label="DNI · Anverso"
            hint="Foto clara de la parte delantera. Deben leerse claramente los datos."
            kind="dni_front"
            uploadedPreview={uploads.dni_front}
            onUpload={uploadDocument}
          />
          <PhotoField
            label="DNI · Reverso"
            hint="Foto clara de la parte trasera."
            kind="dni_back"
            uploadedPreview={uploads.dni_back}
            onUpload={uploadDocument}
          />

          <div className="rounded-2xl border border-neutral-200 bg-white p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <div className="text-sm font-bold text-neutral-900">Psicotécnico</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">Certificado de aptitud psicofísica.</div>
              </div>
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-neutral-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={psicoDeferred}
                  onChange={(e) => setPsicoDeferred(e.target.checked)}
                  className="h-4 w-4 accent-orange-500"
                  data-testid="alta-psico-deferred"
                />
                Aún no lo tengo
              </label>
            </div>
            {psicoDeferred ? (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex items-start gap-2">
                <ClockIcon className="h-4 w-4 mt-0.5 shrink-0" />
                Podrás subirlo más tarde. Te recordaremos hacerlo en las próximas 72 horas.
              </div>
            ) : (
              <PhotoField
                label="Foto del psicotécnico"
                hint="Foto o escaneo del documento completo."
                kind="psicotecnico"
                uploadedPreview={uploads.psicotecnico}
                onUpload={uploadDocument}
              />
            )}
          </div>
        </section>

        {/* V1.33-D · Enviar otro documento (dentro del alta) */}
        <ExtraDocumentUploader token={token} />

        {/* Consents */}
        <section className="bg-white rounded-3xl shadow-sm border border-neutral-100 p-5 sm:p-7 space-y-3" data-testid="alta-consents">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-4 w-4 text-orange-500" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">Consentimientos</h2>
          </div>

          <label className="flex items-start gap-3 rounded-xl border border-neutral-200 p-3 hover:bg-neutral-50 cursor-pointer transition-colors">
            <input
              type="checkbox"
              checked={consentLopd}
              onChange={(e) => setConsentLopd(e.target.checked)}
              className="h-5 w-5 mt-0.5 accent-orange-500 shrink-0"
              data-testid="alta-consent-lopd"
            />
            <span className="text-xs text-neutral-700 leading-snug">
              <b>Protección de datos (LOPD).</b> He leído y acepto la política de privacidad de Natur Autoescuela y consiento el tratamiento de mis datos personales para la gestión de mi expediente.
            </span>
          </label>

          <label className="flex items-start gap-3 rounded-xl border border-neutral-200 p-3 hover:bg-neutral-50 cursor-pointer transition-colors">
            <input
              type="checkbox"
              checked={consentComms}
              onChange={(e) => setConsentComms(e.target.checked)}
              className="h-5 w-5 mt-0.5 accent-orange-500 shrink-0"
              data-testid="alta-consent-comms"
            />
            <span className="text-xs text-neutral-700 leading-snug">
              <b>Comunicaciones.</b> Acepto recibir avisos y notificaciones sobre mis clases, exámenes y trámites por WhatsApp, SMS o email.
            </span>
          </label>

          <label className="flex items-start gap-3 rounded-xl border border-neutral-200 p-3 hover:bg-neutral-50 cursor-pointer transition-colors">
            <input
              type="checkbox"
              checked={consentTram}
              onChange={(e) => setConsentTram(e.target.checked)}
              className="h-5 w-5 mt-0.5 accent-orange-500 shrink-0"
              data-testid="alta-consent-tramitacion"
            />
            <span className="text-xs text-neutral-700 leading-snug">
              <b>Tramitación.</b> Autorizo a Natur Autoescuela a tramitar en mi nombre solicitudes, tasas y documentación con la Dirección General de Tráfico.
            </span>
          </label>
        </section>

        {/* Submit */}
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="w-full h-14 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base inline-flex items-center justify-center gap-2 shadow-sm disabled:opacity-40 transition-colors"
          data-testid="alta-submit"
        >
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          Enviar mi ficha
        </button>

        <p className="text-center text-[11px] text-neutral-400 pb-6">
          Natur Autoescuela · Tu formación, en tus manos.
        </p>
      </div>
    </div>
  );
}
